import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { trpc } from '@org/clients/school';
import { communityTrpc } from '@org/clients/community';
import {
  EventAgenda,
  EventDetailCard,
  extractFirstUrl,
  stripUrlFromLocation,
  type AgendaItem,
  type EventAgendaLabels,
  type EventDetailCardLabels,
} from '@org/ui';

const MAX_ITEMS = 6;

type ScheduledAgendaItem = AgendaItem & {
  /** Internal class route, when the item is linked to a class. */
  classId: string | null;
  /** Raw Event.eventKind when known — drives the detail-card kind label. */
  eventKind: string | null;
  category: string | null;
  description: string | null;
  location: string | null;
};

const badgeKindForEvent = (eventKind: string | null | undefined): AgendaItem['badgeKind'] => {
  switch (eventKind) {
    case 'group-class':
      return 'group';
    case 'private-class':
    case 'tutoring':
      return 'personal';
    default:
      return 'event';
  }
};

export function ScheduledWidget({
  seeAllHref,
  className,
}: {
  /** Where the "See all" link goes (the student calendar page). */
  seeAllHref: string;
  className?: string;
}) {
  const { t } = useTranslation('admin');
  const { t: tp } = useTranslation('p2e');
  const navigate = useNavigate();
  const [selectedItem, setSelectedItem] = useState<ScheduledAgendaItem | null>(null);
  const { data: classData, isLoading: classesLoading } = trpc.classes.getDashboardClasses.useQuery();
  const { data: communityData, isLoading: communityLoading } = communityTrpc.events.upcoming.useQuery(
    { limit: 10 },
    { staleTime: 60_000 },
  );

  const isLoading = classesLoading || communityLoading;

  const items = useMemo<ScheduledAgendaItem[]>(() => {
    const nowMs = Date.now();
    const merged: ScheduledAgendaItem[] = [];

    // Class items carry the class link and are the preferred representation
    // when the same session also appears in the community events feed. Dedupe
    // primarily by the shared eventId (exact); the (start|title) key is a
    // fallback for rows where the id is missing.
    const classKeys = new Set<string>();
    const classEventIds = new Set<string>();
    const keyOf = (startIso: string, title: string) =>
      `${new Date(startIso).getTime()}|${title.trim().toLowerCase()}`;

    for (const cls of classData ?? []) {
      const title = cls.name ?? t('p2e.classes.carousel.fallbackTitle', { defaultValue: 'Class' });
      classKeys.add(keyOf(cls.eventStartTime, title));
      if (cls.eventId) classEventIds.add(cls.eventId);
      merged.push({
        id: `class-${cls.classId}-${cls.eventStartTime}`,
        title,
        startTime: cls.eventStartTime,
        endTime: cls.eventEndTime ?? null,
        badgeKind: badgeKindForEvent(cls.eventKind),
        host: { name: cls.hostName ?? null, avatarUrl: cls.hostAvatarUrl ?? null },
        // Students must not see how many people were invited: only the host's
        // avatar is shown (the "+N" bubble is hidden when the count is 0).
        othersCount: 0,
        isLive: cls.isLive,
        joinHref: extractFirstUrl(cls.eventLocation),
        href: null,
        classId: cls.classId,
        eventKind: cls.eventKind ?? null,
        category: null,
        description: cls.description ?? null,
        location: cls.eventLocation ?? null,
      });
    }

    // Merge scheduled community events of every kind (workshops, group/private
    // sessions not linked to a Class), skipping any already surfaced as a class.
    for (const event of communityData ?? []) {
      if (classEventIds.has(event.id) || classKeys.has(keyOf(event.startsAt, event.title))) continue;
      const startMs = new Date(event.startsAt).getTime();
      const endMs = event.endsAt ? new Date(event.endsAt).getTime() : startMs + 60 * 60 * 1000;
      const isLive = startMs <= nowMs && nowMs < endMs;
      merged.push({
        id: `event-${event.id}`,
        title: event.title,
        startTime: event.startsAt,
        endTime: event.endsAt ?? null,
        badgeKind: badgeKindForEvent(event.eventKind),
        host: { name: event.hostName ?? null, avatarUrl: event.hostAvatarUrl ?? null },
        othersCount: 0,
        isLive,
        joinHref: extractFirstUrl(event.location),
        href: null,
        classId: event.classId ?? null,
        eventKind: event.eventKind ?? null,
        category: event.category ?? null,
        description: event.description ?? null,
        location: event.location ?? null,
      });
    }

    return merged
      .filter((item) => {
        const startMs = new Date(item.startTime).getTime();
        if (!Number.isFinite(startMs)) return false;
        // Drop past items unless currently live.
        const endMs = item.endTime ? new Date(item.endTime).getTime() : startMs + 60 * 60 * 1000;
        return endMs >= nowMs || item.isLive;
      })
      .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())
      .slice(0, MAX_ITEMS);
  }, [classData, communityData, t]);

  const labels = useMemo<EventAgendaLabels>(
    () => ({
      title: t('p2e.dashboard.scheduled.title'),
      empty: t('p2e.dashboard.scheduled.empty'),
      seeAll: t('p2e.dashboard.scheduled.seeAll'),
      today: t('p2e.dashboard.today'),
      tomorrow: t('p2e.dashboard.tomorrow'),
      startsInMinutes: (minutes) => t('p2e.dashboard.scheduled.startsInMinutes', { count: minutes }),
      startsAt: (time) => t('p2e.dashboard.scheduled.startsAt', { time }),
      kind: {
        group: t('p2e.dashboard.scheduled.kind.group'),
        personal: t('p2e.dashboard.scheduled.kind.personal'),
        event: t('p2e.dashboard.scheduled.kind.event'),
      },
      join: tp('calendar.detail.joinLink'),
    }),
    [t, tp],
  );

  const detailLabels = useMemo<EventDetailCardLabels>(
    () => ({
      date: tp('calendar.detail.date'),
      time: tp('calendar.detail.time'),
      teacher: tp('calendar.detail.teacher'),
      location: tp('calendar.detail.location'),
      join: tp('calendar.detail.joinLink'),
      goToClass: tp('calendar.detail.goToClass'),
      live: tp('calendar.detail.live'),
    }),
    [tp],
  );

  const kindLabelFor = (item: ScheduledAgendaItem) =>
    item.eventKind
      ? tp(`calendar.kinds.${item.eventKind}`, { defaultValue: item.eventKind.replace('-', ' ') })
      : labels.kind[item.badgeKind];

  return (
    <>
      <EventAgenda
        items={items}
        isLoading={isLoading}
        labels={labels}
        seeAllHref={seeAllHref}
        className={className}
        onSelectItem={(item) => setSelectedItem(item as ScheduledAgendaItem)}
      />

      <EventDetailCard
        event={
          selectedItem
            ? {
                title: selectedItem.title,
                kindLabel: kindLabelFor(selectedItem),
                categoryLine: selectedItem.category,
                startTime: selectedItem.startTime,
                endTime: selectedItem.endTime,
                hostName: selectedItem.host.name,
                hostAvatarUrl: selectedItem.host.avatarUrl,
                locationText: stripUrlFromLocation(selectedItem.location),
                description: selectedItem.description,
                joinUrl: selectedItem.joinHref ?? null,
              }
            : null
        }
        labels={detailLabels}
        onClose={() => setSelectedItem(null)}
        onGoToClass={
          selectedItem?.classId
            ? () => {
                const classId = selectedItem.classId!;
                setSelectedItem(null);
                navigate({ to: '/classes/$classId', params: { classId } });
              }
            : undefined
        }
      />
    </>
  );
}
